/**
 * The staff session.
 *
 * STAFF ONLY - unlike the knowledge base and the forum, which sign in either identity space through
 * one form. This app is the CRM: every endpoint behind it refuses a portal token, so offering a
 * portal sign-in here would only produce a session that cannot read anything.
 *
 * Only the REFRESH token is persisted, never the access token. The trade-off is the one the
 * OpenSilver client documents: a long-lived credential in localStorage is readable by any script on
 * the origin, and at least the short-lived one is not lying around too.
 *
 * THE KEY IS THIS APP'S OWN (`jetsoncrm.app.refresh`), not the retired OpenSilver client's
 * `jetsoncrm.refreshToken`. Refresh tokens rotate single-use, and while both clients were deployed a
 * shared key would have had them redeem one token between them - straight into the server's reuse
 * detection, which revokes the whole family. `discardRetiredToken` removes the old one unredeemed.
 */
import type { AuthResponse, EnterTenantResponse, TenantDto, UserDto } from '../contracts.gen.js';
import * as storage from '../storage.js';

/**
 * Same-origin, always. The app is served by the same Kestrel process that serves the API, so there
 * is no cross-origin call to configure - and therefore no CORS surface and no API address to get
 * wrong. That is a real simplification over the OpenSilver client, which had to resolve its API base
 * at runtime because its dev server ran on a different port.
 */
const API = '';

const STORE_TOKEN = 'jetsoncrm.app.refresh';
/**
 * The organization a host operator is working in, per tab. Session storage, not local: two tabs may
 * be in two organizations at once, and a tab closed tomorrow should not reopen inside one.
 */
const STORE_ENTERED = 'jetsoncrm.app.entered';
const RETIRED_TOKEN = 'jetsoncrm.refreshToken';

/**
 * Removes the OpenSilver client's stored refresh token, unredeemed.
 *
 * It is a live credential for as long as it sits there, and nothing reads it any more. Redeeming it
 * instead would sign the person straight in, but a browser that still has the old client open in
 * another tab - cached from before the swap - would then race this one on the same rotated token
 * and trip the server's reuse detection, signing both out. One sign-in after the swap is the cheaper
 * outcome.
 */
export function discardRetiredToken(): void {
  storage.remove(RETIRED_TOKEN);
}

/**
 * How close to expiry a token is treated as expired.
 *
 * Wide enough that a request built now and answered a few seconds later is still inside the token's
 * life, and that the sidebar's once-a-minute lockout poll never straddles the boundary. This exists
 * because the browser logs a 401 BEFORE the refresh-and-replay succeeds, so a perfectly healthy
 * session showed a console error once per token lifetime - which was twice misdiagnosed as a
 * page-load race in the OpenSilver app before the server log settled it.
 */
const REFRESH_MARGIN_MS = 90_000;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let accessToken: string | null = null;
let refreshToken: string | null = null;
let expiresAtMs: number | null = null;
let user: UserDto | null = null;

/**
 * A host operator working inside one organization.
 *
 * A host operator's own session (role Host) reaches only the host's routes. To work in an
 * organization they ENTER it: the server hands back a short-lived administrator token for that
 * organization and no refresh token - the host session is the long-lived one, and entering again is
 * how this one is renewed. So `bearer()` sends this token while it exists, and the stored refresh
 * token stays the host's.
 */
interface Entered {
  tenant: TenantDto;
  token: string;
  expiresAtMs: number | null;
  user: UserDto;
}
let entered: Entered | null = null;

/** Fires when the session begins, ends, or changes user, so the shell can re-render. */
const listeners: Array<() => void> = [];
export function onSessionChanged(fn: () => void): void {
  listeners.push(fn);
}
function notify(): void {
  for (const fn of listeners) fn();
}

/**
 * Whether the signed-in staff user is an administrator. Pages use it to hide what the server would
 * refuse a technician anyway - an entry that opens a refusal reads as broken.
 */
export function isAdmin(): boolean {
  return currentUser()?.role?.toLowerCase() === 'admin';
}

/** The person signed in, as the page in front of them sees it: inside an organization, its stand-in. */
export function currentUser(): UserDto | null {
  return entered?.user ?? user;
}

/** A host operator, whether or not they are working inside an organization right now. */
export function isHostOperator(): boolean {
  return user?.role === 'Host';
}

/** A host operator's own session, outside every organization: only the host pages apply. */
export function isHostOnly(): boolean {
  return isHostOperator() && entered === null;
}

export function enteredTenant(): TenantDto | null {
  return entered?.tenant ?? null;
}
export function isSignedIn(): boolean {
  return user !== null;
}
export function mustChangePassword(): boolean {
  return user?.mustChangePassword ?? false;
}

/** The bearer token, for the callers that cannot go through `request` - uploads and the hub. */
export function bearer(): string | null {
  return entered?.token ?? accessToken;
}

function adopt(auth: AuthResponse): void {
  accessToken = auth.accessToken;
  refreshToken = auth.refreshToken;
  expiresAtMs = readExpiry(auth.accessToken);
  if (refreshToken) storage.write(STORE_TOKEN, refreshToken);
  else storage.remove(STORE_TOKEN);
  user = auth.user ?? user;
}

function clearSession(): void {
  entered = null;
  storage.removeSession(STORE_ENTERED);
  accessToken = null;
  refreshToken = null;
  expiresAtMs = null;
  user = null;
  storage.remove(STORE_TOKEN);
  notify();
}

/**
 * The `exp` claim of a JWT, decoded locally.
 *
 * No validation - the server does that. This only decides when to ask for a new one, so an
 * unreadable token yields null, which leaves the refresh-on-401 path as the only mechanism, exactly
 * as it was before pre-emptive refresh existed.
 */
function readExpiry(jwt: string | null): number | null {
  const exp = readClaims(jwt)?.exp;
  return typeof exp === 'number' ? exp * 1000 : null;
}

function readClaims(jwt: string | null): Record<string, unknown> | null {
  try {
    const payload = jwt?.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** The organization the bearer token belongs to (its `tid` claim), or null outside every one. */
export function tenantId(): number | null {
  const tid = Number(readClaims(bearer())?.tid);
  return Number.isInteger(tid) && tid > 0 ? tid : null;
}

/**
 * The single in-flight refresh.
 *
 * A promise rather than a boolean lock, because that is what makes two concurrent callers await the
 * SAME redeem. Two redeems of one rotated token is precisely the reuse detection above, and it is
 * reachable in the ordinary way: the presence poll and a page load can both find the token stale in
 * the same tick.
 */
let refreshing: Promise<boolean> | null = null;

function expiringSoon(): boolean {
  return expiresAtMs !== null && Date.now() >= expiresAtMs - REFRESH_MARGIN_MS;
}

/**
 * Serializes a redeem across every tab of this browser, so several tabs can stay signed in at once.
 *
 * The tabs share one stored refresh token, and it rotates single-use. Without coordination a tab
 * redeems the copy it read at load after another tab has already rotated it - which the server
 * reads as theft and answers by revoking the whole family, signing out every tab. Inside the lock
 * the stored token is the one redeemed: it is the newest in the chain, whichever tab wrote it.
 * Where the Web Locks API is missing the redeem still prefers the stored token, which closes all
 * but the window of two tabs refreshing in the same instant.
 */
function withRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  return locks ? (locks.request(STORE_TOKEN, work) as Promise<T>) : work();
}

async function redeem(): Promise<boolean> {
  return withRefreshLock(redeemNewest);
}

async function redeemNewest(): Promise<boolean> {
  // A signed-in tab that finds nothing stored was signed out by another tab. Redeeming its own copy
  // anyway would present a revoked token, and the reuse detection that answers it revokes this
  // user's sessions on every other device too.
  const stored = storage.read(STORE_TOKEN);
  if (!stored && user !== null && storage.usable()) {
    clearSession();
    return false;
  }
  const token = stored ?? refreshToken;
  if (!token) return false;
  try {
    const response = await fetch(`${API}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token })
    });
    if (!response.ok) {
      clearSession();
      return false;
    }
    const previousId = user?.id ?? null;
    adopt((await response.json()) as AuthResponse);
    // A mid-session refresh replaces the same user silently. A session RESTORE is a real sign-in,
    // and so is another tab having signed in as someone else: the shell has to hear about both.
    if (previousId !== (user?.id ?? null)) notify();
    return true;
  } catch {
    // A network failure is not a rejected token: keep what we have and let the request fail on its
    // own terms, so a momentary outage does not sign somebody out.
    return false;
  }
}

function refreshOnce(): Promise<boolean> {
  refreshing ??= redeem().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/**
 * Refreshes the access token BEFORE it expires, if it is about to. Called ahead of every request, so
 * none ever goes out carrying an expired token - see REFRESH_MARGIN_MS for why that matters.
 */
export async function ensureFresh(): Promise<void> {
  if (isSignedIn() && refreshToken && expiringSoon()) await refreshOnce();
  if (entered && entered.expiresAtMs !== null && Date.now() >= entered.expiresAtMs - REFRESH_MARGIN_MS)
    await reEnterOnce();
}

let reEntering: Promise<boolean> | null = null;
function reEnterOnce(): Promise<boolean> {
  const id = entered?.tenant.id;
  if (id === undefined) return Promise.resolve(false);
  reEntering ??= enter(id, false).finally(() => {
    reEntering = null;
  });
  return reEntering;
}

async function enter(tenantId: number, announce: boolean): Promise<boolean> {
  if (!isHostOperator()) return false;
  if (expiringSoon()) await refreshOnce();
  let response: Response;
  try {
    response = await fetch(`${API}/api/host/tenants/${tenantId}/enter`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken ?? ''}` },
      body: ''
    });
  } catch {
    // A network failure: keep what we have and let the request fail on its own terms.
    return false;
  }
  if (!response.ok) {
    const message = errorFrom(await response.text()) ?? `Could not open the organization (${response.status}).`;
    const wasInside = entered !== null;
    leaveTenantLocal();
    if (wasInside) notify();
    if (announce) throw new ApiError(response.status, message);
    return false;
  }
  const answer = (await response.json()) as EnterTenantResponse;
  const changed = entered?.tenant.id !== tenantId;
  entered = {
    tenant: answer.tenant,
    token: answer.accessToken,
    expiresAtMs: readExpiry(answer.accessToken),
    user: answer.user
  };
  storage.writeSession(STORE_ENTERED, String(tenantId));
  if (changed || announce) notify();
  return true;
}

/**
 * Starts working inside an organization. Throws an ApiError carrying the server's sentence - the
 * organization is suspended, or did not start.
 */
export async function enterTenant(tenantId: number): Promise<void> {
  await enter(tenantId, true);
}

function leaveTenantLocal(): void {
  entered = null;
  storage.removeSession(STORE_ENTERED);
}

/** Back to the host's own pages. */
export function leaveTenant(): void {
  if (!entered) return;
  leaveTenantLocal();
  notify();
}

/** Re-enters the organization this tab was working in before a reload, if any. */
async function resumeEntered(): Promise<void> {
  if (!isHostOperator()) return;
  const id = Number(storage.readSession(STORE_ENTERED));
  if (Number.isInteger(id) && id > 0) await enter(id, false);
}

/** Signs in. Resolves with null on success, or the message to show. */
export async function signIn(email: string, password: string): Promise<string | null> {
  try {
    const response = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    const text = await response.text();
    if (!response.ok) return errorFrom(text) ?? `Sign-in failed (${response.status}).`;
    adopt(JSON.parse(text) as AuthResponse);
    notify();
    return null;
  } catch {
    return 'Could not reach the server. Is the API running?';
  }
}

/**
 * The API's debugger-only auto sign-in.
 *
 * Returns false in production, where the endpoint is a 404 - which is the whole mechanism, so this
 * needs no environment check of its own. Mirrors the OpenSilver app's startup, where it is the
 * reason a developer never sees the login screen.
 */
export async function tryDevSignIn(): Promise<boolean> {
  try {
    const response = await fetch(`${API}/api/auth/dev-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: ''
    });
    if (!response.ok) return false;
    const auth = (await response.json()) as AuthResponse;
    if (!auth?.accessToken) return false;
    adopt(auth);
    // Development signs in as the host operator. Working in the default organization is what a
    // developer almost always wants, so that is where a tab with no remembered choice lands.
    if (isHostOperator() && !storage.readSession(STORE_ENTERED)) storage.writeSession(STORE_ENTERED, '1');
    await resumeEntered();
    notify();
    return true;
  } catch {
    return false;
  }
}

/** Resumes the previous session from the stored refresh token, so F5 does not sign anybody out. */
export async function tryRestoreSession(): Promise<boolean> {
  if (isSignedIn()) return true;
  const stored = storage.read(STORE_TOKEN);
  if (!stored) return false;
  refreshToken = stored;
  const ok = (await refreshOnce()) && isSignedIn();
  if (ok) await resumeEntered();
  return ok && isSignedIn();
}

/** Changes the password. Resolves with null on success, or the message to show. */
export async function changePassword(current: string, next: string): Promise<string | null> {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
    // A host operator's password is the host's, even while working inside an organization.
    const route = isHostOperator() ? '/api/host/auth/change-password' : '/api/auth/change-password';
    const response = await fetch(`${API}${route}`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ currentPassword: current, newPassword: next })
    });
    const text = await response.text();
    if (!response.ok) return errorFrom(text) ?? `Password change failed (${response.status}).`;
    // Success returns a fresh pair with the flag cleared, and revokes the old family server-side.
    adopt(JSON.parse(text) as AuthResponse);
    notify();
    return null;
  } catch {
    return 'Could not reach the server.';
  }
}

export async function signOut(): Promise<void> {
  const token = refreshToken;
  clearSession();
  if (!token) return;
  try {
    await fetch(`${API}/api/auth/logout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: token })
    });
  } catch {
    /* best effort: the local session has already ended */
  }
}

/** Ends the local session without telling the server - for a refresh the server has rejected. */
export function signOutLocal(): void {
  clearSession();
}

/** Retries a 401 once, after a refresh. Returns true when the caller should replay the request. */
export async function recoverFrom401(): Promise<boolean> {
  if (!isSignedIn()) return false;
  // Inside an organization the token that failed is the entered one; entering again renews it.
  if (entered) return reEnterOnce();
  if (await refreshOnce()) return true;
  signOutLocal();
  return false;
}

/** Pulls the server's `{ error }` out of a failed body, or null if it is not shaped that way. */
export function errorFrom(text: string): string | null {
  try {
    const body = JSON.parse(text) as { error?: unknown };
    return typeof body.error === 'string' && body.error ? body.error : null;
  } catch {
    return null;
  }
}

export const API_BASE = API;
